import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { parseCSV } from "@/lib/format";
import { BranchSelect, LoadingButton } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, FileText, AlertTriangle } from "lucide-react";
import ExcelJS from "exceljs";

const DATE_FORMAT_OPTIONS = [
  { value: "MDY", label: "MM/DD/YYYY", hint: "BO / US-style export" },
  { value: "DMY", label: "DD/MM/YYYY", hint: "Malaysia / UK-style export" },
  { value: "YMD", label: "YYYY-MM-DD", hint: "ISO format" },
];

const excelCellValue = (cell) => {
  const value = cell.value;
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (value && typeof value === "object") {
    if ("result" in value) return value.result instanceof Date ? value.result.toISOString().slice(0, 10) : value.result;
    if ("text" in value) return value.text;
    if (Array.isArray(value.richText)) return value.richText.map((part) => part.text).join("");
  }
  return value ?? "";
};

const parseExcel = async (file) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) throw new Error("The workbook has no worksheet");
  const matrix = [];
  sheet.eachRow({ includeEmpty: false }, (row) => matrix.push(row.values.slice(1).map((_, index) => excelCellValue(row.getCell(index + 1)))));
  if (!matrix.length) return { headers: [], rows: [] };
  const headers = matrix[0].map((value, index) => String(value || `Column ${index + 1}`).trim());
  const rows = matrix.slice(1).filter((values) => values.some((value) => String(value ?? "").trim())).map((values) => Object.fromEntries(headers.map((header, index) => [header, values[index] ?? ""])));
  return { headers, rows };
};

const datePreview = (value, format) => {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  const raw = String(value ?? "").trim().split("T")[0].split(" ")[0];
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d ? `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}` : null;
  }
  const parts = raw.split(/[/-]/).map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) return null;
  const [a, b, c] = parts;
  const [y, m, d] = format === "DMY" ? [c, b, a] : format === "MDY" ? [c, a, b] : [a, b, c];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d ? `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}` : null;
};

/**
 * Generic CSV importer with column mapping.
 * props: fields=[{key,label,required}], endpoint, kind ('credit'|'publicbank'),
 *        supportsAllBranches (bool), onDone()
 */
export default function CsvImport({ fields, endpoint, kind, supportsAllBranches = false, extraPayload = {}, defaultDateFormat, onDone }) {
  const fileRef = useRef();
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [mapping, setMapping] = useState({});
  const [defaultBranch, setDefaultBranch] = useState("");
  const [allBranches, setAllBranches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [dateFormat, setDateFormat] = useState(defaultDateFormat || (kind === "credit" ? "MDY" : "DMY"));

  const amountValue = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return null;
    const negative = raw.startsWith("(") && raw.endsWith(")");
    const n = Number(raw.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? (negative ? -Math.abs(n) : n) : null;
  };
  const amountColumn = mapping.net_amount || mapping.gross_amount;
  const previewValues = amountColumn ? rows.map((r) => amountValue(r[amountColumn])) : [];
  const recognized = previewValues.filter((v) => v !== null);
  const previewTotal = recognized.reduce((sum, value) => sum + value, 0);

  const autoMap = (hdrs) => {
    const m = {};
    fields.forEach((f) => {
      const hit = hdrs.find((h) => h.toLowerCase().replace(/[^a-z]/g, "").includes(f.key.replace(/_/g, "")));
      if (hit) m[f.key] = hit;
    });
    return m;
  };

  const dateField = fields.find((field) => field.key.includes("date"));
  const dateColumn = dateField ? mapping[dateField.key] : null;
  const recognizedDates = dateColumn ? rows.map((row) => datePreview(row[dateColumn], dateFormat)).filter(Boolean).sort() : [];
  const dateMonths = [...new Set(recognizedDates.map((date) => date.slice(0, 7)))];

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      let parsed;
      if (/\.xlsx$/i.test(file.name)) parsed = await parseExcel(file);
      else parsed = parseCSV(await file.text());
      const { headers, rows } = parsed;
      setHeaders(headers); setRows(rows); setResult(null);
      setMapping(autoMap(headers));
    } catch (error) {
      setHeaders([]); setRows([]);
      toast.error(error?.message || "Unable to read this sales file");
    }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/sales/templates", { params: { kind } });
      setTemplates(data);
    } catch (e) { /* ignore */ }
  };

  const applyTemplate = (id) => {
    const tpl = templates.find((t) => t.id === id);
    if (tpl) setMapping(tpl.mapping);
  };

  const saveTemplate = async () => {
    if (!templateName) return toast.error("Enter a template name");
    try {
      await api.post("/sales/templates", { name: templateName, kind, mapping });
      toast.success("Template saved");
      setTemplateName(""); loadTemplates();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async () => {
    const missing = fields.filter((f) => f.required && !mapping[f.key]);
    if (missing.length) return toast.error(`Map required columns: ${missing.map((m) => m.label).join(", ")}`);
    if (!rows.length) return toast.error("No rows to import");
    if (dateColumn && recognizedDates.length !== rows.length) return toast.error(`Date validation failed: ${rows.length - recognizedDates.length} row(s) do not match ${DATE_FORMAT_OPTIONS.find((option) => option.value === dateFormat)?.label}.`);
    setLoading(true);
    try {
      const payload = { rows, mapping, file_name: fileName, date_format: dateFormat, ...extraPayload };
      if (kind === "publicbank") { payload.all_branches = allBranches; payload.default_branch_id = allBranches ? null : defaultBranch || null; }
      else { payload.default_branch_id = defaultBranch || null; }
      const { data } = await api.post(endpoint, payload);
      setResult(data);
      toast.success("Import complete");
      onDone && onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="border-2 border-dashed border-slate-300 rounded-sm p-6 text-center bg-slate-50">
        <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx" onChange={onFile} className="hidden" data-testid="csv-file-input" />
        <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" strokeWidth={1.5} />
        <Button variant="secondary" onClick={() => fileRef.current?.click()} data-testid="csv-choose-btn">Choose sales file</Button>
        {fileName && <div className="mt-2 text-sm text-slate-600 flex items-center justify-center gap-1"><FileText className="h-4 w-4" /> {fileName} · {rows.length} rows</div>}
        <div className="text-xs text-slate-400 mt-1">CSV, TXT and Excel .xlsx supported. The first worksheet and first row are used.</div>
      </div>

      {headers.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-slate-800">Map columns</h3>
            <div className="flex items-center gap-2">
              <Select onValueChange={(v) => { applyTemplate(v); }} onOpenChange={(o) => o && loadTemplates()}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="template-select"><SelectValue placeholder="Load template" /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {fields.map((f) => (
              <div key={f.key}>
                <Label className="text-xs">{f.label} {f.required && <span className="text-red-500">*</span>}</Label>
                <Select value={mapping[f.key] || undefined} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "__none__" ? undefined : v }))}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid={`map-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— none —</SelectItem>
                    {headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {amountColumn && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-sm border border-blue-100 bg-blue-50 p-3" data-testid="import-preview">
              <div><div className="text-[11px] uppercase text-slate-500">Rows detected</div><div className="font-semibold">{rows.length}</div></div>
              <div><div className="text-[11px] uppercase text-slate-500">Amounts recognized</div><div className="font-semibold">{recognized.length}</div></div>
              <div><div className="text-[11px] uppercase text-slate-500">Recognized total</div><div className="font-semibold text-blue-700">RM {previewTotal.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
              {recognized.length !== rows.length && <div className="sm:col-span-3 flex gap-2 text-xs text-amber-700"><AlertTriangle className="h-4 w-4 shrink-0" /> {rows.length - recognized.length} row(s) have blank or invalid amounts and will be rejected.</div>}
            </div>
          )}

          {dateField && (
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="date-format-preview">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-56"><Label className="text-xs">Date format in this file</Label><Select value={dateFormat} onValueChange={setDateFormat}><SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger><SelectContent>{DATE_FORMAT_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label} · {option.hint}</SelectItem>)}</SelectContent></Select></div>
                <div className="text-xs text-slate-600">Recognized: <strong>{recognizedDates.length}/{rows.length}</strong>{recognizedDates.length > 0 && <> · Range: <strong>{recognizedDates[0]}</strong> to <strong>{recognizedDates[recognizedDates.length - 1]}</strong> · Month(s): <strong>{dateMonths.join(", ")}</strong></>}</div>
              </div>
              <div className="flex gap-2 text-xs text-amber-800"><AlertTriangle className="h-4 w-4 shrink-0" />Choose the source file's actual format. Ambiguous dates are never guessed by the server.</div>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-4 border-t border-slate-100 pt-4">
            {supportsAllBranches && (
              <div className="flex items-center gap-2">
                <Switch checked={allBranches} onCheckedChange={setAllBranches} data-testid="all-branches-switch" />
                <span className="text-sm text-slate-600">Report contains multiple branches (map via alias)</span>
              </div>
            )}
            {!allBranches && (
              <div className="w-64">
                <Label className="text-xs">Default branch {supportsAllBranches ? "(this file)" : "(fallback)"}</Label>
                <div className="mt-1"><BranchSelect value={defaultBranch} onChange={setDefaultBranch} testid="import-default-branch" /></div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Input placeholder="Template name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} className="h-9 w-40 text-sm" data-testid="template-name-input" />
              <Button variant="outline" size="sm" onClick={saveTemplate} data-testid="save-template-btn">Save mapping</Button>
            </div>
            <LoadingButton loading={loading} onClick={submit} className="bg-blue-600 hover:bg-blue-700 ml-auto" data-testid="csv-import-btn">Import {rows.length} rows</LoadingButton>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="import-result">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Import result</h3>
          <div className="flex flex-wrap gap-2">
            {"imported" in result && <Badge className="bg-emerald-100 text-emerald-800">Imported: {result.imported}</Badge>}
            {"inserted" in result && <Badge className="bg-emerald-100 text-emerald-800">Inserted: {result.inserted}</Badge>}
            {"updated" in result && <Badge className="bg-blue-100 text-blue-800">Updated: {result.updated}</Badge>}
            {"duplicate" in result && <Badge className="bg-amber-100 text-amber-800">Duplicates: {result.duplicate}</Badge>}
            {"rejected" in result && <Badge className="bg-red-100 text-red-800">Rejected: {result.rejected}</Badge>}
          </div>
          {result.rejected_rows?.length > 0 && (
            <div className="mt-3 text-xs text-slate-500">
              {result.rejected_rows.slice(0, 5).map((r, i) => <div key={i}>· {r.reason}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
